import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { CustomerSiteLogService, CustomersService, SleepService } from "@services";
import { CustomerSiteLogAssetModel, DropdownModel, ICustomerSiteLogChecklistModel, ICustomerSiteLogModel, ICustomerModel, IDropdownModel } from "@models";
import { SlickToastService } from "@slick-components";
@Component({
	selector: "site-log-edit",
	templateUrl: "site-log-edit.component.html",
	styleUrls: ["site-log-edit.component.scss"],
	providers: [CustomerSiteLogService, CustomersService, SlickToastService]
})
export class SiteLogEditComponent implements OnChanges {
	@Input() customerModel: ICustomerModel;

	siteLogModel: ICustomerSiteLogModel;
	checklistModel: ICustomerSiteLogChecklistModel;
	jobItems: IDropdownModel[] = [];
	selectedJobId: number;
	spinnerStatus: string;
	pdfSpinnerStatus: string;
	isLoading = false;
	isLoadingProgress = false;

	constructor(
		private customerSiteLogService: CustomerSiteLogService,
		private customersService: CustomersService,
		private slickToastService: SlickToastService) { }

	async ngOnChanges(changes: SimpleChanges) {
		if (changes.customerModel)
			await this.loadSiteLog();
	}

	async loadSiteLog() {
		if (!this.customerModel?.customerId) {
			this.siteLogModel = null;
			this.checklistModel = null;
			this.jobItems = [];
			this.selectedJobId = null;
			return;
		}

		this.isLoading = true;
		try {
			this.siteLogModel = await this.customerSiteLogService.getCustomerSiteLog(this.customerModel.customerId);
			if (!this.siteLogModel.assets)
				this.siteLogModel.assets = [];
			await this.loadCustomerJobs();
		} finally {
			this.isLoading = false;
		}
	}

	async loadCustomerJobs() {
		const history = await this.customersService.getCustomerJobHistory(this.customerModel.customerId, " ");
		const seenJobIds = new Set<number>();
		this.jobItems = [];

		for (const item of history ?? []) {
			if (!item?.jobId || seenJobIds.has(item.jobId))
				continue;

			seenJobIds.add(item.jobId);
			const label = item.jobName?.trim() || `Job ${item.jobId}`;
			this.jobItems.push(new DropdownModel(item.jobId, label));
		}

		if (this.selectedJobId && !seenJobIds.has(this.selectedJobId)) {
			this.selectedJobId = null;
			this.checklistModel = null;
		}
	}

	async onJobSelected(jobId: number) {
		this.selectedJobId = jobId;
		if (!jobId) {
			this.checklistModel = null;
			return;
		}

		this.isLoadingProgress = true;
		try {
			this.checklistModel = await this.customerSiteLogService.getSiteLogChecklist(
				this.customerModel.customerId, jobId, 0);
		} finally {
			this.isLoadingProgress = false;
		}
	}

	async onDownloadPdf() {
		if (!this.selectedJobId || !this.customerModel?.customerId)
			return;

		if (!this.siteLogModel?.assets?.length) {
			this.slickToastService.showWarning("Add fleet assets before generating a Site Log PDF.");
			return;
		}

		try {
			this.pdfSpinnerStatus = "spin";
			await SleepService.sleep(200);
			let url = await this.customerSiteLogService.generateSiteLogPdf(this.customerModel.customerId, this.selectedJobId);
			if (typeof url === "string")
				url = url.replace(/^"|"$/g, "");
			window.open(url, "_blank");
			this.pdfSpinnerStatus = "ok";
		} catch {
			this.pdfSpinnerStatus = "error";
			this.slickToastService.showWarning("Could not generate Site Log PDF.");
		}
	}

	get progressPercent(): number {
		const p = this.checklistModel?.percentComplete ?? 0;
		return Math.min(100, Math.max(0, Math.round(p)));
	}

	get progressRingBackground(): string {
		return `conic-gradient(#198754 ${this.progressPercent}%, #dee2e6 0)`;
	}

	addAsset() {
		if (!this.siteLogModel)
			return;
		this.siteLogModel.assets.push(new CustomerSiteLogAssetModel());
	}

	removeAsset(index: number) {
		this.siteLogModel.assets.splice(index, 1);
	}

	async saveSiteLog() {
		if (!this.customerModel?.customerId) {
			this.slickToastService.showWarning("Save the customer first before configuring Site Log.");
			return;
		}

		const assets = (this.siteLogModel.assets ?? [])
			.map((x, i) => ({ ...x, sortOrder: i, name: (x.name ?? "").trim() }));

		if (assets.some(x => x.name.length > 100)) {
			this.spinnerStatus = "error";
			this.slickToastService.showDanger("Each fleet name must be 100 characters or fewer.");
			return;
		}

		const namedAssets = assets.filter(x => x.name);
		const normalizedNames = namedAssets.map(x => x.name.toLowerCase());
		if (normalizedNames.length !== new Set(normalizedNames).size) {
			this.spinnerStatus = "error";
			this.slickToastService.showDanger("Fleet asset names must be unique.");
			return;
		}

		this.siteLogModel.assets = namedAssets;

		try {
			this.spinnerStatus = "spin";
			await SleepService.sleep(300);
			this.siteLogModel = await this.customerSiteLogService.saveCustomerSiteLog(this.customerModel.customerId, this.siteLogModel);
			this.spinnerStatus = "ok";
			this.slickToastService.showSuccess("Site Log saved.");
		} catch {
			this.spinnerStatus = "error";
		}
	}
}
